// The Transfer Partner Network (Phase 5 PR 5.4, Q4): the canonical list and
// its sort, read by /transfer (and re-exported by the retired
// campus-redesign-v1 experiment until Phase 7 removed it).
import arkansasLogo from '@/public/images/arkansas-university.png'
import arizonaLogo from '@/public/images/arizona-university.png'
import butlerLogo from '@/public/images/butler-university.png'
import centralFloridaLogo from '@/public/images/florida-university.png'
import centralMichiganLogo from '@/public/images/michigan-university.png'
import cincinnatiLogo from '@/public/images/cincinnati-university.png'
import coloradoLogo from '@/public/images/colorado-university.png'
import excelsiorLogo from '@/public/images/excelsior-university.png'
import floridaInternationalLogo from '@/public/images/florida-international-university.png'
import indianaStateLogo from '@/public/images/indiana-university.png'
import indianaWesleyanLogo from '@/public/images/iwu-university.png'
import loyolaLogo from '@/public/images/loyola-university.png'
import marylandLogo from '@/public/images/maryland-university.png'
import memphisLogo from '@/public/images/memphis-university.png'
import mississippiLogo from '@/public/images/mississippi-university.png'
import morehouseLogo from '@/public/images/morehouse-university.png'
import nationalLouisLogo from '@/public/images/nlu-university.png'
import njitLogo from '@/public/images/njit-university.png'
import nyuSpsLogo from '@/public/images/nyu-sps-university.png'
import northTexasLogo from '@/public/images/texas-university.png'
import northernArizonaLogo from '@/public/images/nau-university.png'
import oaklandLogo from '@/public/images/oakland-university.png'
import pointParkLogo from '@/public/images/pointpark-university.png'
import rollinsLogo from '@/public/images/rollins-college.png'
import rutgersLogo from '@/public/images/rutgers-school.png'
import southernNewHampshireLogo from '@/public/images/hampshire-university.png'
import texasSouthernLogo from '@/public/images/tsu-university.png'
import texasStateLogo from '@/public/images/texas-state-university.png'
import texasTechLogo from '@/public/images/texas-tech-university.png'
import tulaneLogo from '@/public/images/tulane-university.png'
import umassLogo from '@/public/images/umass-university.png'
import toledoLogo from '@/public/images/toledo-university.png'
import sanAntonioLogo from '@/public/images/utsan-antonio-univrsity.png'
import uvaLogo from '@/public/images/uva-school.png'
import vcuLogo from '@/public/images/vcu-university.png'
import westVirginiaLogo from '@/public/images/wvu-university.png'
import wichitaLogo from '@/public/images/wichita-university.png'

/**
 * @typedef {object} TransferPartner
 * @property {import('next/image').StaticImageData} image
 * @property {string} name
 */

export const umassTransferPartner = {
  image: umassLogo,
  name: 'UMass Global University',
}

export const toledoTransferPartner = {
  image: toledoLogo,
  name: 'University of Toledo',
}

export const nyuSpsTransferPartner = {
  image: nyuSpsLogo,
  name: 'New York University - School of Professional Studies',
}

/**
 * Partners are listed by institution name, so a leading "University of" is
 * ignored when sorting (e.g. "University of Toledo" sorts under T).
 *
 * @param {string} name
 * @returns {string}
 */
export const transferPartnerSortKey = name =>
  name.replace(/^University of\s+/i, '')

/**
 * @param {{name: string}} a
 * @param {{name: string}} b
 * @returns {number}
 */
export const compareTransferPartners = (a, b) =>
  transferPartnerSortKey(a.name).localeCompare(
    transferPartnerSortKey(b.name),
    'en',
    {sensitivity: 'base'},
  )

/** @type {TransferPartner[]} */
const unsortedTransferPartners = [
  {image: arizonaLogo, name: 'Arizona State University'},
  {image: arkansasLogo, name: 'University of Arkansas Fort Smith'},
  {image: butlerLogo, name: 'Butler University'},
  {image: centralFloridaLogo, name: 'University of Central Florida'},
  {image: centralMichiganLogo, name: 'Central Michigan University'},
  {image: cincinnatiLogo, name: 'University of Cincinnati'},
  {image: coloradoLogo, name: 'Colorado State University Global'},
  {image: excelsiorLogo, name: 'Excelsior University'},
  {image: floridaInternationalLogo, name: 'Florida International University'},
  {image: indianaStateLogo, name: 'Indiana State University'},
  {image: indianaWesleyanLogo, name: 'Indiana Wesleyan University'},
  {image: loyolaLogo, name: 'Loyola University New Orleans'},
  {image: marylandLogo, name: 'University of Maryland Global Campus'},
  {image: memphisLogo, name: 'University of Memphis'},
  {image: mississippiLogo, name: 'Mississippi State University'},
  {image: morehouseLogo, name: 'Morehouse College'},
  {image: nationalLouisLogo, name: 'National Louis University'},
  {image: njitLogo, name: 'New Jersey Institute of Technology'},
  nyuSpsTransferPartner,
  {image: northTexasLogo, name: 'University of North Texas'},
  {image: northernArizonaLogo, name: 'Northern Arizona University'},
  {image: oaklandLogo, name: 'Oakland University'},
  {image: pointParkLogo, name: 'Point Park University'},
  {image: rollinsLogo, name: 'Rollins College'},
  {image: rutgersLogo, name: 'Rutgers Business School-Newark'},
  {
    image: southernNewHampshireLogo,
    name: 'Southern New Hampshire University',
  },
  {image: texasSouthernLogo, name: 'Texas Southern University'},
  {image: texasStateLogo, name: 'Texas State University'},
  {image: texasTechLogo, name: 'Texas Tech University'},
  toledoTransferPartner,
  {
    image: tulaneLogo,
    name: 'Tulane University - School of Professional Advancement',
  },
  umassTransferPartner,
  {image: sanAntonioLogo, name: 'UT San Antonio'},
  {
    image: uvaLogo,
    name: 'UVA - School of Continuing and Professional Studies',
  },
  {image: vcuLogo, name: 'Virginia Commonwealth University'},
  {image: westVirginiaLogo, name: 'West Virginia University'},
  {image: wichitaLogo, name: 'Wichita State University'},
]

/** @type {TransferPartner[]} */
export const transferPartners = [...unsortedTransferPartners].sort(
  compareTransferPartners,
)
